import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Calendar as CalendarIcon, MapPin, Clock, Tag } from "lucide-react";
import { Calendar } from "../components/ui/calendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  forum: "#2196F3",
  gathering: "#4CAF50",
  fundraiser: "#E53935",
  workshop: "#FFB300",
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(undefined);

  useEffect(() => {
    axios.get(`${API}/events`).then((r) => setEvents(r.data || [])).catch(() => {});
  }, []);

  const eventDates = useMemo(() => events.map((e) => new Date(e.date + "T00:00:00")), [events]);

  const filtered = useMemo(() => {
    if (!selected) return events;
    const key = selected.toISOString().slice(0, 10);
    return events.filter((e) => e.date === key);
  }, [selected, events]);

  return (
    <div data-testid="page-events">
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Events Calendar</div>
        <h1 className="mt-4 font-heading font-bold tracking-tighter text-[#1F2937] text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
          Gather with us,<br />
          <span className="brush-underline brush-yellow">be yourself.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[#4B5563] leading-relaxed">
          Forums, workshops, picnics, and joyful sensory-friendly parties — all designed for every ability.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-12 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5">
          <div className="rounded-3xl bg-white border border-black/5 p-5">
            <Calendar
              data-testid="events-calendar"
              mode="single"
              selected={selected}
              onSelect={setSelected}
              modifiers={{ event: eventDates }}
              modifiersStyles={{
                event: { fontWeight: 700, color: "#1F2937", backgroundColor: "#FFB30033", borderRadius: 12 },
              }}
              className="mx-auto"
            />
            <div className="mt-5 flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-[#6B7280]">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#FFB300]" />
                Days with events
              </div>
              {selected && (
                <button
                  data-testid="events-clear-filter-btn"
                  onClick={() => setSelected(undefined)}
                  className="text-[#2196F3] hover:underline font-medium"
                >
                  Clear filter
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="flex items-center gap-2 text-sm text-[#6B7280] mb-5">
            <CalendarIcon className="w-4 h-4" strokeWidth={1.6} />
            {selected ? `Events on ${selected.toLocaleDateString()}` : "All upcoming events"}
            <span className="ml-auto text-[#1F2937] font-semibold" data-testid="events-count">{filtered.length} event{filtered.length === 1 ? "" : "s"}</span>
          </div>

          {filtered.length === 0 && (
            <div className="rounded-3xl bg-[#F4F1EA] border border-black/5 p-8 text-center text-[#6B7280]" data-testid="events-empty">
              No events on that day. Try clearing the filter to see all upcoming events.
            </div>
          )}

          <div className="space-y-4">
            {filtered.map((e) => {
              const color = CATEGORY_COLORS[e.category] || "#2196F3";
              return (
                <div key={e.id} data-testid={`event-card-${e.id}`} className="rounded-3xl bg-white border border-black/5 p-7 hover:-translate-y-0.5 hover:shadow-sm">
                  <div className="flex flex-wrap items-center gap-3 text-xs">
                    <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-semibold uppercase tracking-wider"
                          style={{ backgroundColor: `${color}15`, color, border: `1px solid ${color}33` }}>
                      <Tag className="w-3 h-3" /> {e.category}
                    </span>
                    <span className="text-[#6B7280]">{new Date(e.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</span>
                  </div>
                  <h3 className="mt-3 font-heading text-2xl font-semibold tracking-tight text-[#1F2937]">{e.title}</h3>
                  <p className="mt-2 text-[#4B5563] leading-relaxed">{e.description}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-5 text-sm text-[#6B7280]">
                    {e.time && <div className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" strokeWidth={1.6} /> {e.time}</div>}
                    <div className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" strokeWidth={1.6} /> {e.location}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
